import React, { useEffect, useState } from 'react';
import axios from '../../utils/axiosConfig';
import Content from '../../components/Content';

// Follow the app theme (light by default, dark via [data-theme]) rather than
// hardcoding colours: surfaces use --white, text --black, muted --grey, borders
// --light-grey, accent --primary-color.
const box = { background: 'var(--white)', border: '1px solid var(--light-grey)', borderRadius: 8, padding: 20, marginBottom: 20, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)' };
const input = { width: '100%', padding: '8px 10px', background: 'var(--white)', border: '1px solid var(--grey2)', borderRadius: 6, color: 'var(--black)', marginTop: 4 };
const label = { display: 'block', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--grey)', marginBottom: 2 };
const btn = { background: 'var(--primary-color)', color: '#fff', border: 'none', borderRadius: 6, padding: '9px 16px', cursor: 'pointer', fontWeight: 600 };
const th = { textAlign: 'left', padding: '8px 10px', color: 'var(--grey)', fontSize: 12, textTransform: 'uppercase', borderBottom: '1px solid var(--light-grey)' };
const td = { padding: '8px 10px', borderBottom: '1px solid var(--light-grey)', color: 'var(--black)' };
const actBtn = { color: '#fff', border: 'none', borderRadius: 5, padding: '5px 11px', cursor: 'pointer', fontSize: 12, fontWeight: 600, marginRight: 6 };

// Semantic status colours, chosen to read on the light surface (and stay legible
// in dark mode).
const SUCCESS = '#0f9d58';
const WARN = '#b45309';
const DANGER = '#c81212';

// License status → colour. valid green, grace amber, anything else (invalid/
// unknown) red so an unlicensed or lapsing box stands out.
const licenseColor = (status) => {
  const s = String(status || '').toLowerCase();
  if (s === 'valid') return SUCCESS;
  if (s === 'grace') return WARN;
  return DANGER;
};

export default function Watchers() {
  const [watchers, setWatchers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [form, setForm] = useState({ name: '', serial_number: '', hostname: '', company_id: '', license_key: '', ot_licensed: false, firewall_licensed: false, blocking_licensed: false, qcommander_licensed: false, purge_legacy: false, tunnel_enabled: false, access_server: '', server_port: '', reverse_port: '', console_reverse_port: '', ssh_user: '', ssh_pass: '', country: 'US', state: 'ID', city: 'Idaho Falls', latitude: '43.4917', longitude: '-112.0339' });
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [denied, setDenied] = useState(false);
  const [editForm, setEditForm] = useState(null);   // { id, name, serial_number, hostname } | null
  const [editBusy, setEditBusy] = useState(false);
  const [editErr, setEditErr] = useState('');

  const load = async () => {
    try {
      const { data } = await axios.get('/api/watchers');
      if (data.status) setWatchers(data.watchers || []);
    } catch (e) {
      if (e?.response?.status === 403) setDenied(true);
    }
  };

  const setActive = async (id, active) => {
    try {
      await axios.post(`/api/watchers/${id}/set-active`, { active });
      load();
    } catch (e) { /* leave the table as-is on failure */ }
  };

  const setLicense = async (id, field, value) => {
    try {
      await axios.post(`/api/watchers/${id}/licenses`, { [field]: value });
      load();
    } catch (e) { /* leave the table as-is on failure */ }
  };

  const saveEdit = async () => {
    if (!editForm) return;
    setEditBusy(true); setEditErr('');
    try {
      const { data } = await axios.post(`/api/watchers/${editForm.id}/edit`, {
        name: editForm.name, serial_number: editForm.serial_number, hostname: editForm.hostname,
      }, { headers: { 'Content-Type': 'application/json' } });
      if (data.status) { setEditForm(null); load(); }
      else setEditErr(data.msg || 'Update failed.');
    } catch (e) {
      setEditErr(e?.response?.data?.msg || e.message);
    } finally {
      setEditBusy(false);
    }
  };

  const deleteWatcher = async (id, name) => {
    if (!window.confirm(`Delete Watcher "${name}" and ALL of its data (traffic, assets, alerts)? This cannot be undone.`)) return;
    try {
      await axios.post(`/api/watchers/${id}/delete`);
      load();
    } catch (e) { /* leave the table as-is on failure */ }
  };

  useEffect(() => {
    load();
    axios.get('/api/watchers/companies').then(({ data }) => {
      if (data.status) {
        setCompanies(data.companies || []);
        if (data.companies && data.companies.length === 1) {
          setForm((f) => ({ ...f, company_id: String(data.companies[0].id) }));
        }
      }
    }).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setResult(null); setBusy(true);
    try {
      const payload = { ...form };
      if (payload.company_id) payload.company_id = Number(payload.company_id);
      const { data } = await axios.post('/api/watchers/register', payload, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (data.status) {
        setResult(data);
        setForm({ name: '', serial_number: '', hostname: '', company_id: form.company_id, license_key: '', ot_licensed: false, firewall_licensed: false, blocking_licensed: false, qcommander_licensed: false, purge_legacy: false, tunnel_enabled: false, access_server: '', server_port: '', reverse_port: '', console_reverse_port: '', ssh_user: '', ssh_pass: '', country: 'US', state: 'ID', city: 'Idaho Falls', latitude: '43.4917', longitude: '-112.0339' });
        load();
      } else {
        setError(data.msg || 'Registration failed.');
      }
    } catch (err) {
      setError(err?.response?.data?.msg || err.message);
    } finally {
      setBusy(false);
    }
  };

  const [copied, setCopied] = useState('');
  const copy = async (text, which) => {
    // navigator.clipboard exists only in a secure context (HTTPS or localhost).
    // The C2C is often served over plain HTTP on the LAN, where it is undefined,
    // so fall back to the execCommand path, which works over HTTP.
    let ok = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        ok = true;
      }
    } catch (e) { ok = false; }
    if (!ok) {
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        ok = document.execCommand('copy');
        document.body.removeChild(ta);
      } catch (e) { ok = false; }
    }
    setCopied(ok ? which : '');
    if (ok) setTimeout(() => setCopied(''), 1500);
  };

  if (denied) {
    return (
      <Content currentMenu='system' headerTitle='Register Watcher'>
        <div style={{ padding: 24, color: 'var(--grey)' }}>Registering Watchers is available to provider (Frontline) staff only.</div>
      </Content>
    );
  }

  return (
    <Content currentMenu='system' headerTitle='Register Watcher'>
      <div style={{ padding: 24, color: 'var(--black)' }}>
      <p style={{ color: 'var(--grey)', marginTop: 0 }}>Register an appliance and mint its token, then install it with the command shown.</p>

      <div style={box}>
        <h3 style={{ marginTop: 0 }}>Register a Watcher</h3>
        <form onSubmit={submit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div><label style={label}>Name</label>
              <input style={input} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Main Plant" required /></div>
            <div><label style={label}>Serial</label>
              <input style={input} value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} placeholder="3157" /></div>
            <div><label style={label}>Hostname (WatcherID)</label>
              <input style={input} value={form.hostname} onChange={(e) => setForm({ ...form, hostname: e.target.value })} placeholder="Watcher3157" /></div>
            <div><label style={label}>License Key (optional)</label>
              <input style={input} value={form.license_key} onChange={(e) => setForm({ ...form, license_key: e.target.value })} placeholder="baked into the install command" /></div>
            {companies.length !== 1 && (
              <div><label style={label}>Company</label>
                <select style={input} value={form.company_id} onChange={(e) => setForm({ ...form, company_id: e.target.value })} required>
                  <option value="">Select a company…</option>
                  {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select></div>
            )}
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={label}>Licenses</label>
              <div style={{ display: 'flex', gap: 24, marginTop: 4 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: 'var(--text)' }}>
                  <input type="checkbox" checked={form.ot_licensed}
                    onChange={(e) => setForm({ ...form, ot_licensed: e.target.checked })} />
                  OT Sensor
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: 'var(--text)' }}>
                  <input type="checkbox" checked={form.firewall_licensed}
                    onChange={(e) => setForm({ ...form, firewall_licensed: e.target.checked })} />
                  Firewall
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: 'var(--text)' }}>
                  <input type="checkbox" checked={form.blocking_licensed}
                    onChange={(e) => setForm({ ...form, blocking_licensed: e.target.checked })} />
                  Blocking
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: 'var(--text)' }}>
                  <input type="checkbox" checked={form.qcommander_licensed}
                    onChange={(e) => setForm({ ...form, qcommander_licensed: e.target.checked })} />
                  QCommander Ticketing
                </label>
              </div>
            </div>
            <div><label style={label}>Country</label>
              <input style={input} value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="US" /></div>
            <div><label style={label}>State / Province</label>
              <input style={input} value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="NV" /></div>
            <div><label style={label}>City</label>
              <input style={input} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Las Vegas" /></div>
            <div><label style={label}>Latitude</label>
              <input style={input} value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} placeholder="36.1699" /></div>
            <div><label style={label}>Longitude</label>
              <input style={input} value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} placeholder="-115.1398" /></div>
          </div>

          <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--border-color, #e5e7eb)' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.purge_legacy} onChange={(e) => setForm({ ...form, purge_legacy: e.target.checked })} style={{ width: 16, height: 16, accentColor: 'var(--primary-color)' }} />
              <span style={{ ...label, color: 'var(--primary-color)', margin: 0 }}>Remove legacy Watcher on install</span>
            </label>
            <p style={{ fontSize: 12, color: 'var(--grey)', margin: '6px 0 0' }}>
              Turn on when converting an old box: the installer purges the legacy MorningWatch/MySQL Watcher (its services, cron, <strong>MySQL/MariaDB incl. its data</strong>, and /opt/Watcher) before installing NextGen. Leave off for a fresh box — it&apos;s a no-op when no legacy is present, but it deletes the MySQL data when it is.
            </p>
          </div>

          <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--border-color, #e5e7eb)' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.tunnel_enabled} onChange={(e) => setForm({ ...form, tunnel_enabled: e.target.checked })} style={{ width: 16, height: 16, accentColor: 'var(--primary-color)' }} />
              <span style={{ ...label, color: 'var(--primary-color)', margin: 0 }}>Set up Access Tunnel (AutoSSH)</span>
            </label>
            <p style={{ fontSize: 12, color: 'var(--grey)', margin: '6px 0 10px' }}>
              Leave off for a box that already has its reverse tunnel — the install command won&apos;t touch AutoSSH. Turn on to configure reverse-SSH so you can reach this appliance via an access server. The SSH username/password are used once (via ssh-copy-id) to authorize the box&apos;s key — the password is baked into the install command and never stored.
            </p>
            {form.tunnel_enabled && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div><label style={label}>Access Server</label>
                  <input style={input} value={form.access_server} onChange={(e) => setForm({ ...form, access_server: e.target.value })} placeholder="watchercon.flccorp.online" /></div>
                <div><label style={label}>Server Port</label>
                  <input style={input} value={form.server_port} onChange={(e) => setForm({ ...form, server_port: e.target.value })} placeholder="2202" /></div>
                <div><label style={label}>SSH Reverse Port (→ :22)</label>
                  <input style={input} value={form.reverse_port} onChange={(e) => setForm({ ...form, reverse_port: e.target.value })} placeholder="30078" /></div>
                <div><label style={label}>GUI Reverse Port (→ :8443, optional)</label>
                  <input style={input} value={form.console_reverse_port} onChange={(e) => setForm({ ...form, console_reverse_port: e.target.value })} placeholder="e.g. 38443" /></div>
                <div><label style={label}>SSH Username</label>
                  <input style={input} value={form.ssh_user} onChange={(e) => setForm({ ...form, ssh_user: e.target.value })} placeholder="Watcher157" /></div>
                <div><label style={label}>SSH Password (for ssh-copy-id)</label>
                  <input style={input} type="password" value={form.ssh_pass} onChange={(e) => setForm({ ...form, ssh_pass: e.target.value })} placeholder="used once, not stored" /></div>
              </div>
            )}
          </div>

          <div style={{ marginTop: 16 }}><button style={btn} type="submit" disabled={busy}>{busy ? 'Registering…' : 'Register Watcher'}</button></div>
        </form>
        {error && <p style={{ color: DANGER, marginTop: 12 }}>{error}</p>}
      </div>

      {result && (
        <div style={{ ...box, borderColor: 'var(--primary-color)' }}>
          <h3 style={{ marginTop: 0 }}>{result.appliance.name} registered</h3>
          <p style={{ color: WARN }}>The token is shown once. Record it now — only its hash is stored.</p>
          <label style={label}>Token</label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <code style={{ ...input, fontFamily: 'monospace', flex: 1 }}>{result.token}</code>
            <button type="button" style={btn} onClick={() => copy(result.token, 'token')}>{copied === 'token' ? 'Copied' : 'Copy'}</button>
          </div>
          <label style={{ ...label, marginTop: 14 }}>Install command</label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <code style={{ ...input, fontFamily: 'monospace', flex: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{result.installCommand}</code>
            <button type="button" style={btn} onClick={() => copy(result.installCommand, 'cmd')}>{copied === 'cmd' ? 'Copied' : 'Copy'}</button>
          </div>
        </div>
      )}

      <div style={box}>
        <h3 style={{ marginTop: 0 }}>Registered Watchers</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={th}>Name</th><th style={th}>Company</th><th style={th}>Serial</th><th style={th}>Version</th><th style={th}>Local IP</th><th style={th}>Public IP</th><th style={th}>Access Server</th><th style={th}>License</th><th style={th}>Licenses</th><th style={th}>Reporting</th><th style={th}>Status</th><th style={th}>Last Seen</th><th style={th}>Actions</th></tr></thead>
          <tbody>
            {watchers.length === 0 && <tr><td style={{ ...td, color: 'var(--grey)' }} colSpan={13}>No Watchers registered yet.</td></tr>}
            {watchers.map((w) => (
              <tr key={w.id} style={w.active === false ? { opacity: 0.55 } : undefined}>
                <td style={td}>{w.name}</td>
                <td style={td}>{w.company || '—'}</td>
                <td style={td}>{w.serial_number || '—'}</td>
                <td style={td}>{w.version || '—'}</td>
                <td style={td}>{w.local_ip || '—'}</td>
                <td style={td}>{w.public_ip || '—'}</td>
                <td style={td}>
                  {w.access_server
                    ? (<span title={w.reverse_port ? `-R ${w.reverse_port}:localhost:22` : undefined}>
                        {w.access_server}{w.reverse_port ? `:${w.reverse_port}` : ''}{' '}
                        <span style={{ color: w.tunnel_connected ? SUCCESS : WARN, fontWeight: 700 }}>
                          {w.tunnel_connected ? '●' : '○'}
                        </span>
                      </span>)
                    : '—'}
                </td>
                <td style={td}>
                  {w.license_status
                    ? <span style={{ color: licenseColor(w.license_status), fontWeight: 600 }} title={w.license_expires_at ? `Expires ${w.license_expires_at}` : undefined}>{w.license_status}</span>
                    : '—'}
                </td>
                <td style={td}>
                  <button type="button" title="Toggle OT Sensor license"
                    onClick={() => setLicense(w.id, 'ot_licensed', !w.ot_licensed)}
                    style={{ ...actBtn, background: w.ot_licensed ? '#2f7d4f' : '#6b7280' }}>OT</button>
                  <button type="button" title="Toggle Firewall license"
                    onClick={() => setLicense(w.id, 'firewall_licensed', !w.firewall_licensed)}
                    style={{ ...actBtn, background: w.firewall_licensed ? '#2f7d4f' : '#6b7280' }}>FW</button>
                  <button type="button" title="Toggle Blocking license"
                    onClick={() => setLicense(w.id, 'blocking_licensed', !w.blocking_licensed)}
                    style={{ ...actBtn, background: w.blocking_licensed ? '#2f7d4f' : '#6b7280' }}>BLK</button>
                  <button type="button" title="Toggle QCommander Ticketing license"
                    onClick={() => setLicense(w.id, 'qcommander_licensed', !w.qcommander_licensed)}
                    style={{ ...actBtn, background: w.qcommander_licensed ? '#2f7d4f' : '#6b7280' }}>QC</button>
                </td>
                <td style={td}>{w.reporting ? 'Yes' : <span style={{ color: WARN }}>Never</span>}</td>
                <td style={td}>
                  {w.active === false
                    ? <span style={{ color: WARN }}>Disabled</span>
                    : <span style={{ color: w.online ? SUCCESS : 'var(--grey)' }}>{w.online ? 'Online' : 'Offline'}</span>}
                </td>
                <td style={td}>{w.last_seen_at || '—'}</td>
                <td style={td}>
                  {w.active === false
                    ? <button type="button" style={{ ...actBtn, background: '#2f7d4f' }} onClick={() => setActive(w.id, true)}>Enable</button>
                    : <button type="button" style={{ ...actBtn, background: '#6b7280' }} onClick={() => setActive(w.id, false)}>Disable</button>}
                  <button type="button" style={{ ...actBtn, background: '#2f5f8a' }}
                    onClick={() => setEditForm({ id: w.id, name: w.name || '', serial_number: w.serial_number || '', hostname: w.hostname || '' })}>Edit</button>
                  <button type="button" style={{ ...actBtn, background: '#a20000' }} onClick={() => deleteWatcher(w.id, w.name)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editForm && (
        <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, left: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => !editBusy && setEditForm(null)}>
          <div style={{ ...box, width: 420, maxWidth: '90vw', marginBottom: 0 }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Edit Watcher</h3>
            {editErr && <div style={{ color: DANGER, marginBottom: 10 }}>{editErr}</div>}
            <div style={{ display: 'grid', gap: 12 }}>
              <div><label style={label}>Name</label>
                <input style={input} value={editForm.name} autoFocus
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /></div>
              <div><label style={label}>Serial</label>
                <input style={input} value={editForm.serial_number}
                  onChange={(e) => setEditForm({ ...editForm, serial_number: e.target.value })} placeholder="3157" /></div>
              <div><label style={label}>Hostname (WatcherID)</label>
                <input style={input} value={editForm.hostname}
                  onChange={(e) => setEditForm({ ...editForm, hostname: e.target.value })} placeholder="Watcher3157" /></div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
              <button type="button" disabled={editBusy} onClick={() => setEditForm(null)}
                style={{ ...btn, background: 'transparent', color: 'var(--black)', border: '1px solid var(--light-grey)' }}>Cancel</button>
              <button type="button" disabled={editBusy || !editForm.name.trim()} onClick={saveEdit} style={btn}>{editBusy ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
      </div>
    </Content>
  );
}
